/**
 * Pure dest-list merge used by the dest store.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — keep sync merge out of
 * the Zustand module so the add-vs-echo race is unit-testable.
 * Per rule: ci-only-failures-fix-the-product-first (ai.knowledge/global) —
 * empty studio echo after a local add is a product wipe, not an e2e flake.
 * Per rule: absent-data-gates-permissively (ai.knowledge/global) — unknown
 * studio state must not enforce an empty dest list.
 * Per rule: settle-every-terminal-branch (ai.knowledge/global) — pending-add
 * and pending-delete settle on confirm, local delete, and re-add.
 */

export type ReconcilableDestination = {
  readonly id: string;
  readonly selectedSourceId?: string | undefined;
};

export type PendingEchoSets = {
  readonly pendingAddIds: readonly string[];
  readonly pendingDeleteIds: readonly string[];
};

export type ReconcileDestinationsResult<T extends ReconcilableDestination> = {
  readonly destinations: T[];
  readonly pendingAddIds: string[];
  readonly pendingDeleteIds: string[];
};

const uniquePush = (ids: readonly string[], id: string): string[] =>
  ids.includes(id) ? [...ids] : [...ids, id];

/** Local add: wait for a studio echo; a re-add cancels a pending delete. */
export function markPendingAdd(pending: PendingEchoSets, id: string): PendingEchoSets {
  return {
    pendingAddIds: uniquePush(pending.pendingAddIds, id),
    pendingDeleteIds: pending.pendingDeleteIds.filter((pendingId) => pendingId !== id)
  };
}

/** Local delete: do not resurrect from a stale echo; cancel a pending add. */
export function markPendingDelete(pending: PendingEchoSets, id: string): PendingEchoSets {
  return {
    pendingAddIds: pending.pendingAddIds.filter((pendingId) => pendingId !== id),
    pendingDeleteIds: uniquePush(pending.pendingDeleteIds, id)
  };
}

/**
 * Studio echo is the base. Only a dest still in pendingAddIds is kept when
 * the echo omits it. A dest in pendingDeleteIds is not resurrected from a
 * stale echo. selectedSourceId is local-only and is preserved on overlap.
 */
export function reconcileDestinations<T extends ReconcilableDestination>(
  local: readonly T[],
  global: readonly T[],
  pending: PendingEchoSets
): ReconcileDestinationsResult<T> {
  const pendingAdd = new Set(pending.pendingAddIds);
  const pendingDelete = new Set(pending.pendingDeleteIds);
  // Re-add wins if both flags were set; store helpers keep them exclusive.
  for (const id of pendingAdd) {
    pendingDelete.delete(id);
  }

  const globalIds = new Set(global.map((dest) => dest.id));
  for (const id of [...pendingAdd]) {
    if (globalIds.has(id)) pendingAdd.delete(id);
  }
  for (const id of [...pendingDelete]) {
    if (!globalIds.has(id)) pendingDelete.delete(id);
  }

  const byId = new Map<string, T>();
  for (const dest of global) {
    if (pendingDelete.has(dest.id)) continue;
    byId.set(dest.id, dest);
  }
  for (const dest of local) {
    const echoed = byId.get(dest.id);
    if (!echoed) {
      if (pendingAdd.has(dest.id)) {
        byId.set(dest.id, dest);
      }
      continue;
    }
    // Studio dests do not carry selectedSourceId — keep the local assign.
    if (dest.selectedSourceId && !echoed.selectedSourceId) {
      byId.set(dest.id, { ...echoed, selectedSourceId: dest.selectedSourceId });
    }
  }

  return {
    destinations: [...byId.values()],
    pendingAddIds: [...pendingAdd],
    pendingDeleteIds: [...pendingDelete]
  };
}
